import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { buildCsvFromRows, downloadCsvContent, forceCsvText } from '../utils/csvExport';
import { cn } from '../lib/utils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function getCurrentPayrollMonthStr() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function getPreviousPayrollMonthStr(monthStr) {
  const year = Number(monthStr.slice(0, 4));
  const monthIndex = Number(monthStr.slice(5, 7));
  const d = new Date(year, monthIndex - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function fmtInr(amount) {
  const n = Number(amount) || 0;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

export default function AdminPayrollOverview({ authToken }) {
  const { t } = useLanguage();
  const currentMonth = getCurrentPayrollMonthStr();
  const previousMonth = getPreviousPayrollMonthStr(currentMonth);
  const [month, setMonth] = useState(currentMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [expandedOwners, setExpandedOwners] = useState({});

  const loadOverview = useCallback(async () => {
    if (!authToken) {
      setError(t('sessionExpiredRefresh'));
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/payroll/overview?month=${encodeURIComponent(month)}`,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error || 'Failed');
      }
      setData(json);
      setExpandedOwners({});
    } catch (_err) {
      setData(null);
      setError(t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [authToken, month, t]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const byOwner = useMemo(
    () => (Array.isArray(data?.by_owner) ? data.by_owner : []),
    [data],
  );

  const summary = data?.summary || {
    total_operators: 0,
    total_gross: 0,
    total_net: 0,
  };

  const hasData = !loading && !error && summary.total_operators > 0;

  const toggleOwner = (ownerName) => {
    setExpandedOwners((prev) => ({
      ...prev,
      [ownerName]: !prev[ownerName],
    }));
  };

  const handleExportCsv = async () => {
    if (!hasData) return;
    setExporting(true);
    try {
      const headers = [
        'Owner Name',
        'Owner Phone',
        'Operator Name',
        'Monthly Salary',
        'Gross Payable',
        'Net Payable',
      ];
      const rows = [];
      byOwner.forEach((ownerRow) => {
        (ownerRow.operators || []).forEach((op) => {
          rows.push([
            ownerRow.owner_name,
            forceCsvText(ownerRow.owner_phone),
            op.operator_name,
            op.monthly_salary,
            op.gross_payable,
            op.net_payable,
          ]);
        });
      });
      const csv = buildCsvFromRows(headers, rows);
      await downloadCsvContent(csv, `DE-Admin-Payroll-${month}.csv`);
    } finally {
      setExporting(false);
    }
  };

  const summaryCards = [
    { icon: '👷', val: `${summary.total_operators ?? 0}`, label: t('adminPayrollTotalOperators') },
    { icon: '💰', val: fmtInr(summary.total_gross), label: t('adminPayrollTotalGross') },
    { icon: '✅', val: fmtInr(summary.total_net), label: t('adminPayrollTotalNet') },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="m-0 flex items-center gap-2 text-lg font-bold text-de-gold">
          <Users className="h-5 w-5" aria-hidden />
          {t('adminPayrollOverview')}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {[previousMonth, currentMonth].map((monthOption) => {
            const active = month === monthOption;
            return (
              <button
                key={monthOption}
                type="button"
                onClick={() => setMonth(monthOption)}
                className={cn(
                  'rounded-lg border px-3 py-2 text-xs font-semibold transition-colors',
                  active
                    ? 'border-de-gold bg-de-gold/20 text-de-gold'
                    : 'border-de-gold/35 bg-black/20 text-de-muted hover:border-de-gold/60',
                )}
              >
                {monthOption}
              </button>
            );
          })}
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={!hasData || exporting}
            className={cn(
              'rounded-lg border border-de-gold/40 bg-de-gold/10 px-3 py-2 text-xs font-semibold text-de-gold transition-opacity',
              (!hasData || exporting) && 'cursor-not-allowed opacity-50',
            )}
          >
            {exporting ? t('loading') : t('exportPayrollCsv')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {loading ? (
          <div className="de-ultra-card col-span-full p-5">
            <p className="m-0 text-sm text-de-muted">{t('loading')}</p>
          </div>
        ) : error ? (
          <div className="de-ultra-card col-span-full p-5">
            <p className="m-0 mb-3 text-sm text-de-accent">{error}</p>
            <button
              type="button"
              onClick={loadOverview}
              className="rounded-lg border border-de-gold/40 bg-de-gold/10 px-4 py-2 text-xs font-semibold text-de-gold"
            >
              {t('retryLabel')}
            </button>
          </div>
        ) : (
          summaryCards.map((card) => (
            <div key={card.label} className="de-ultra-card p-5 text-center">
              <p className="m-0 text-2xl">{card.icon}</p>
              <p className="m-0 mt-2 text-xl font-bold text-white">{card.val}</p>
              <p className="m-0 mt-1 text-[11px] uppercase tracking-wide text-de-muted">{card.label}</p>
            </div>
          ))
        )}
      </div>

      <div className="de-ultra-card overflow-hidden">
        <div className="border-b border-de-gold/15 px-5 py-4">
          <h3 className="m-0 text-sm font-bold text-white">{t('adminFleetOwnerCol')}</h3>
        </div>
        <div className="p-4">
          {loading ? (
            <p className="m-0 text-sm text-de-muted">{t('loading')}</p>
          ) : error ? (
            <p className="m-0 text-sm text-de-accent">{error}</p>
          ) : byOwner.length === 0 ? (
            <p className="m-0 text-sm text-de-muted">{t('adminPayrollNoData')}</p>
          ) : (
            <div className="space-y-2">
              {byOwner.map((ownerRow) => {
                const ownerKey = `${ownerRow.owner_name}:${ownerRow.owner_phone}`;
                const expanded = !!expandedOwners[ownerKey];
                return (
                  <div key={ownerKey} className="rounded-xl border border-white/10 bg-black/20">
                    <button
                      type="button"
                      onClick={() => toggleOwner(ownerKey)}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="m-0 truncate text-sm font-bold text-de-gold">{ownerRow.owner_name}</p>
                        <p className="m-0 mt-0.5 text-[11px] text-de-muted">
                          {ownerRow.owner_phone || '—'}
                          {' · '}
                          {(ownerRow.operators || []).length}
                          {' '}
                          {t('adminPayrollTotalOperators').toLowerCase()}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-4 text-xs">
                        <span className="text-de-muted">
                          {t('payrollGross')}:
                          {' '}
                          <strong className="text-white">{fmtInr(ownerRow.owner_total_gross)}</strong>
                        </span>
                        <span className="text-de-muted">
                          {t('payrollNet')}:
                          {' '}
                          <strong className="text-emerald-400">{fmtInr(ownerRow.owner_total_net)}</strong>
                        </span>
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 text-de-gold transition-transform',
                            expanded && 'rotate-180',
                          )}
                          aria-hidden
                        />
                      </div>
                    </button>
                    {expanded && (
                      <div className="border-t border-white/10 px-4 py-3">
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[520px] border-collapse text-xs">
                            <thead>
                              <tr className="border-b border-white/10">
                                {[t('operatorsColOperator'), t('payrollMonthlySalary'), t('payrollGross'), t('payrollNet')].map((h) => (
                                  <th key={h} className="px-2 py-2 text-left font-semibold text-de-muted">{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {(ownerRow.operators || []).map((op) => (
                                <tr key={`${ownerKey}:${op.operator_name}`} className="border-b border-white/5">
                                  <td className="px-2 py-2 font-semibold text-de-text">{op.operator_name}</td>
                                  <td className="px-2 py-2 text-de-text">{fmtInr(op.monthly_salary)}</td>
                                  <td className="px-2 py-2 text-de-text">{fmtInr(op.gross_payable)}</td>
                                  <td className="px-2 py-2 font-semibold text-emerald-400">{fmtInr(op.net_payable)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
